/**
 * Shared by `select` and `checklist` — both persist `{ optionSource, options, multiple }`.
 * The drawer exposes only `options` (`optionSource` stays whatever it already was — the
 * drawer never sets it; `multiple` is implied by the section's own type, not user-editable).
 *
 * Rows are reorderable because the order is what a briefer will see in the list.
 */
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Field, FieldLabel, Input } from '@monksflow/monks-ui';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useRef, type ChangeEvent, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortableList } from '@/shared/hooks';
import { DragHandle } from '@/shared/ui';

export interface OptionsConfigFieldsProps {
  config: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  disabled: boolean;
}

interface OptionRowProps {
  id: string;
  index: number;
  value: string;
  disabled: boolean;
  removable: boolean;
  inputRef?: Ref<HTMLInputElement>;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

function OptionRow(props: OptionRowProps) {
  const { t } = useTranslation('knowledge');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.id,
    disabled: props.disabled,
  });

  const position = props.index + 1;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'flex items-center gap-2 opacity-50' : 'flex items-center gap-2'}
    >
      <DragHandle
        ariaLabel={t('knowledgeHub.briefSectionConfigFields.dragOption', { index: position })}
        attributes={attributes}
        listeners={listeners}
        disabled={props.disabled}
      />
      <Input
        ref={props.inputRef}
        aria-label={t('knowledgeHub.briefSectionConfigFields.optionPlaceholder', {
          index: position,
        })}
        placeholder={t('knowledgeHub.briefSectionConfigFields.optionLabel')}
        value={props.value}
        disabled={props.disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          props.onChange(props.index, event.target.value)
        }
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('knowledgeHub.briefSectionConfigFields.removeOptionNamed', {
          index: position,
        })}
        disabled={props.disabled || !props.removable}
        onClick={() => props.onRemove(props.index)}
      >
        <Trash2Icon aria-hidden className="size-4" />
      </Button>
    </div>
  );
}

export function OptionsConfigFields(props: OptionsConfigFieldsProps) {
  const { t } = useTranslation('knowledge');
  const options = Array.isArray(props.config.options) ? (props.config.options as string[]) : [''];

  // Options are plain strings with no identity of their own, so the index is the only
  // stable handle a drag can address. Reordering rewrites the whole array in one patch,
  // which keeps the ids and the values in step.
  const ids = options.map((_, index) => `option-${index}`);

  const { sensors, handleDragEnd } = useSortableList(ids, ({ oldIndex, newIndex }) => {
    props.onChange({ options: arrayMove(options, oldIndex, newIndex) });
  });

  const updateOption = (index: number, value: string) => {
    const next = [...options];
    next[index] = value;
    props.onChange({ options: next });
  };

  // The option added last, focused once its input mounts so typing carries on from the click.
  const pendingFocusIndex = useRef<number | null>(null);
  const focusIfPending = (index: number) => (input: HTMLInputElement | null) => {
    if (input && pendingFocusIndex.current === index) {
      pendingFocusIndex.current = null;
      input.focus();
    }
  };

  const addOption = () => {
    pendingFocusIndex.current = options.length;
    props.onChange({ options: [...options, ''] });
  };

  const removeOption = (index: number) => {
    const next = options.filter((_, i) => i !== index);
    props.onChange({ options: next.length > 0 ? next : [''] });
  };

  return (
    <Field>
      <FieldLabel>{t('knowledgeHub.briefSectionConfigFields.options')}</FieldLabel>
      <div className="flex flex-col gap-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {options.map((option, index) => (
              <OptionRow
                key={ids[index]}
                id={ids[index]}
                index={index}
                value={option}
                disabled={props.disabled}
                removable={options.length > 1}
                inputRef={focusIfPending(index)}
                onChange={updateOption}
                onRemove={removeOption}
              />
            ))}
          </SortableContext>
        </DndContext>
        {/* Indented past the drag-handle column so it lines up with the inputs it adds to. */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={props.disabled}
          onClick={addOption}
          className="ml-6 self-start"
        >
          <PlusIcon aria-hidden className="size-4" />
          {t('knowledgeHub.briefSectionConfigFields.addOption')}
        </Button>
      </div>
    </Field>
  );
}
